import { ShapePinComponent as ShapePinComponentType } from '@lessonup/pins-shared';
import { styled } from '@lessonup/ui-components';
import React from 'react';
import { isBoxLayout } from '../../types';
import { computeShadowFilter } from '../../utils/effects/shadow/shadow';
import { useEffectivePinComponentSettings } from '../../utils/pinTheme/PinThemeContext';
import { clampStrokeWidth, DEFAULT_SHAPE_LINE_JOIN, scaleStrokeDashArray } from './shapePinComponent.utils';
import { ArrowShape } from './shapes/ArrowShape';
import { EllipseShape } from './shapes/EllipseShape';
import { IconShape } from './shapes/IconShape';
import { PolygonShape } from './shapes/PolygonShape';
import { RectangleShape } from './shapes/RectangleShape';
import { StarShape } from './shapes/StarShape';
import { TriangleShape } from './shapes/TriangleShape';

export interface ShapePinComponentProps {
  pinComponent: ShapePinComponentType;
}

export const ShapePinComponent: React.FC<ShapePinComponentProps> = ({ pinComponent }) => {
  const { settings, layout } = pinComponent;
  const shapeSettings = useEffectivePinComponentSettings(pinComponent.type, {
    settings,
  });
  const box = isBoxLayout(layout) ? layout : null;

  if (!box) {
    return null;
  }

  const fillColor = shapeSettings.fillColor;
  const strokeColor = shapeSettings.borderColor;
  const strokeWidth = clampStrokeWidth(shapeSettings.borderSize ?? 0, box.size);
  const lineCap = shapeSettings.lineCap;
  const lineJoin = shapeSettings.lineJoin ?? DEFAULT_SHAPE_LINE_JOIN;
  const strokeDashArray = shapeSettings.strokeDashArray
    ? scaleStrokeDashArray(shapeSettings.strokeDashArray, strokeWidth)
    : undefined;
  const strokeOpacity = (shapeSettings.strokeOpacity ?? 100) / 100;
  const fillOpacity = (shapeSettings.fillOpacity ?? 100) / 100;
  const opacity = (shapeSettings.transparency ?? 100) / 100;
  const dropShadow = computeShadowFilter(shapeSettings.shadow);

  const renderShape = () => {
    switch (shapeSettings.shapeType) {
      case 'RECTANGLE':
        return <RectangleShape size={box.size} strokeWidth={strokeWidth} cornerRadius={shapeSettings.borderRadius} />;
      case 'ELLIPSE':
        return <EllipseShape size={box.size} strokeWidth={strokeWidth} />;
      case 'TRIANGLE':
        return <TriangleShape size={box.size} strokeWidth={strokeWidth} />;
      case 'STAR':
        return (
          <StarShape
            size={box.size}
            strokeWidth={strokeWidth}
            points={shapeSettings.points}
            innerRadius={shapeSettings.innerRadius}
          />
        );
      case 'POLYGON':
        return <PolygonShape size={box.size} strokeWidth={strokeWidth} points={shapeSettings.points} />;
      case 'ARROW':
        return (
          <ArrowShape
            size={box.size}
            strokeWidth={strokeWidth}
            headAngle={shapeSettings.headAngle}
            bodyThickness={shapeSettings.bodyThickness}
          />
        );
      case 'ICON':
        return <IconShape iconName={shapeSettings.iconName} />;
    }
  };

  return (
    <>
      <StyledSvg
        style={{
          color: fillColor,
          filter: dropShadow ? `drop-shadow(${dropShadow})` : undefined,
          opacity: opacity,
        }}
        width={box.size.width}
        height={box.size.height}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap={lineCap}
        strokeLinejoin={lineJoin}
        strokeDasharray={strokeDashArray}
        strokeOpacity={strokeOpacity}
        opacity={opacity}
        fillOpacity={fillOpacity}
      >
        {renderShape()}
      </StyledSvg>
    </>
  );
};

const StyledSvg = styled.svg`
  position: absolute;
  overflow: visible;
`;
